import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { CheckCircle2 } from 'lucide-react';

const Shades = () => {
  const shades = [
    {
      percentage: '5%',
      name: 'Limo Black',
      description: 'Maximum privacy and heat rejection',
      lightBlocked: '95% light blocked',
      features: ['Supreme privacy', 'Maximum UV protection', 'Excellent heat rejection']
    },
    {
      percentage: '20%',
      name: 'Dark Tint',
      description: 'Perfect balance of privacy and visibility',
      lightBlocked: '80% light blocked',
      features: ['High privacy', 'Superior UV protection', 'Great heat reduction'],
      popular: true
    },
    {
      percentage: '35%',
      name: 'Medium Tint',
      description: 'Ideal for everyday driving',
      lightBlocked: '65% light blocked',
      features: ['Good privacy', 'Excellent UV protection', 'Balanced heat control']
    },
    {
      percentage: '50%',
      name: 'Light Tint',
      description: 'Subtle protection with clear visibility',
      lightBlocked: '50% light blocked',
      features: ['Moderate privacy', 'UV protection', 'Heat reduction']
    }
  ];

  return (
    <section className="py-20 bg-black relative overflow-hidden border-t border-gray-900">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold-600/20 rounded-full blur-[120px]"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-gold-500 font-semibold text-sm uppercase tracking-wider">Shade Selection</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">Available Shades</h2>
          <p className="text-gray-400 text-lg">Select your preferred darkness level</p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {shades.map((shade, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className={`bg-gray-800/30 border-gray-700 hover:border-gold-600/50 transition-all duration-300 hover:transform hover:scale-105 relative h-full ${
                shade.popular ? 'ring-2 ring-gold-600' : ''
              }`}>
                {shade.popular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gold-500 text-black font-bold">
                    Most Popular
                  </Badge>
                )}
                <CardHeader>
                  <div className="text-6xl font-bold text-gold-500 mb-2">{shade.percentage}</div>
                  <CardTitle className="text-xl text-white">{shade.name}</CardTitle>
                  <p className="text-gray-400 text-sm mt-2">{shade.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="mb-4 pb-4 border-b border-slate-800">
                    <p className="text-gray-300 font-semibold">{shade.lightBlocked}</p>
                  </div>
                  <ul className="space-y-2">
                    {shade.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2 text-gray-400 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-gold-500" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center text-gray-500 text-sm"
        >
          * Tint percentages may be subject to local regulations. We ensure all installations comply with state laws.
        </motion.p>
      </div>
    </section>
  );
};

export default Shades;
